import React, { useEffect, useState } from 'react';
import api from '../services/api';

const containerStyle = {
  maxWidth: '760px',
  background: '#ffffff',
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
};

const sectionTitle = {
  fontSize: '1.05rem',
  fontWeight: 600,
  marginTop: '24px',
  marginBottom: '12px',
};

const fieldGroup = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginBottom: '14px',
};

const labelStyle = {
  fontWeight: 600,
  color: '#334155',
};

const inputStyle = {
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #cbd5f5',
  fontSize: '0.95rem',
};

const textareaStyle = {
  ...inputStyle,
  minHeight: '120px',
  resize: 'vertical',
};

const buttonStyle = {
  padding: '12px 18px',
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 600,
  marginTop: '16px',
};

const infoBox = {
  marginTop: '16px',
  padding: '12px 16px',
  borderRadius: '8px',
  fontSize: '0.9rem',
  lineHeight: 1.6,
};

const DEFAULT_SUBJECT_TEMPLATE = '{{project}}사업단 {{name}}님 {{year}}년 {{month}}월 급여명세표를 보내 드립니다.';
const DEFAULT_BODY_TEMPLATE =
  '{{project}}사업단 {{name}}님,\n' +
  '\n' +
  '\n' +
  '{{year}}년 {{month}}월 급여명세표를 보내드립니다.\n' +
  '\n' +
  '첨부 PDF 비밀번호는 생년월일 6글자를 입력해 주세요.\n' +
  '\n' +
  '감사합니다.';

function PayrollEmailSettingsPage() {
  const [form, setForm] = useState({
    senderName: '',
    senderEmail: '',
    smtpHost: '',
    smtpPort: '',
    smtpSecurityMode: 'ssl', // 'none' | 'ssl' | 'starttls'
    smtpUser: '',
    smtpPass: '',
    imapHost: '',
    imapPort: '',
    imapSecurityMode: 'ssl', // 'none' | 'ssl' | 'starttls'
    imapUser: '',
    imapPass: '',
    subjectTemplate: '',
    bodyTemplate: '',
    sendDelayMs: 2000,
  });
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/payroll/settings/email');
      const data = response.data || {};
      setForm({
        senderName: data.senderName || '',
        senderEmail: data.senderEmail || '',
        smtpHost: data.smtp?.host || '',
        smtpPort: data.smtp?.port || '',
        smtpSecurityMode: data.smtp?.securityMode || (data.smtp?.secure ? 'ssl' : 'none'),
        smtpUser: data.smtp?.user || '',
        smtpPass: data.smtp?.pass || '',
        imapHost: data.imap?.host || '',
        imapPort: data.imap?.port || '',
        imapSecurityMode: data.imap?.securityMode || (data.imap?.secure ? 'ssl' : 'none'),
        imapUser: data.imap?.user || '',
        imapPass: data.imap?.pass || '',
        subjectTemplate: data.subjectTemplate || '',
        bodyTemplate: data.bodyTemplate || '',
        sendDelayMs: data.sendDelayMs ?? 2000,
      });
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: '이메일 설정을 불러오지 못했습니다.' });
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const response = await api.put('/payroll/settings/email', {
        ...form,
      });
      setMessage({ type: 'success', text: response.data.message });
    } catch (error) {
      const text = error.response?.data?.message || '저장에 실패했습니다.';
      setMessage({ type: 'error', text });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>이메일 설정</h2>
      <form style={containerStyle} onSubmit={handleSubmit}>
        <div style={fieldGroup}>
          <label style={labelStyle}>발신자 이름</label>
          <input
            style={inputStyle}
            value={form.senderName}
            onChange={(e) => handleChange('senderName', e.target.value)}
            placeholder="예: 원주시니어클럽"
          />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>발신 이메일 주소</label>
          <input
            style={inputStyle}
            value={form.senderEmail}
            onChange={(e) => handleChange('senderEmail', e.target.value)}
            placeholder="예: payroll@domain.com"
          />
        </div>

        <div style={sectionTitle}>SMTP (발송용)</div>
        <div style={fieldGroup}>
          <label style={labelStyle}>SMTP 서버</label>
          <input
            style={inputStyle}
            value={form.smtpHost}
            onChange={(e) => handleChange('smtpHost', e.target.value)}
            placeholder="smtp.example.com"
          />
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ ...fieldGroup, flex: '1 1 160px' }}>
            <label style={labelStyle}>포트</label>
            <input
              type="number"
              style={inputStyle}
              value={form.smtpPort}
              onChange={(e) => handleChange('smtpPort', e.target.value)}
              placeholder="465 또는 587"
            />
          </div>
          <div style={{ ...fieldGroup, flex: '1 1 200px' }}>
            <label style={labelStyle}>보안 방식</label>
            <select
              style={inputStyle}
              value={form.smtpSecurityMode}
              onChange={(e) => handleChange('smtpSecurityMode', e.target.value)}
            >
              <option value="none">없음</option>
              <option value="ssl">SSL (예: 465)</option>
              <option value="starttls">STARTTLS (예: 587)</option>
            </select>
          </div>
        </div>
        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '8px' }}>
          사용하는 메일 서버(회사 메일, 네이버, Gmail 등)의 안내에 맞춰 포트와 보안 방식을 설정하세요.
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>계정 아이디</label>
          <input
            style={inputStyle}
            value={form.smtpUser}
            onChange={(e) => handleChange('smtpUser', e.target.value)}
          />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>계정 비밀번호</label>
          <input
            type="password"
            style={inputStyle}
            value={form.smtpPass}
            onChange={(e) => handleChange('smtpPass', e.target.value)}
          />
        </div>

        <div style={sectionTitle}>IMAP (수신 확인용)</div>
        <div style={fieldGroup}>
          <label style={labelStyle}>IMAP 서버</label>
          <input
            style={inputStyle}
            value={form.imapHost}
            onChange={(e) => handleChange('imapHost', e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ ...fieldGroup, flex: '1 1 160px' }}>
            <label style={labelStyle}>포트</label>
            <input
              type="number"
              style={inputStyle}
              value={form.imapPort}
              onChange={(e) => handleChange('imapPort', e.target.value)}
            />
          </div>
          <div style={{ ...fieldGroup, flex: '1 1 200px' }}>
            <label style={labelStyle}>보안 방식</label>
            <select
              style={inputStyle}
              value={form.imapSecurityMode}
              onChange={(e) => handleChange('imapSecurityMode', e.target.value)}
            >
              <option value="none">없음</option>
              <option value="ssl">SSL (예: 993)</option>
              <option value="starttls">STARTTLS</option>
            </select>
          </div>
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>계정 아이디</label>
          <input
            style={inputStyle}
            value={form.imapUser}
            onChange={(e) => handleChange('imapUser', e.target.value)}
          />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>계정 비밀번호</label>
          <input
            type="password"
            style={inputStyle}
            value={form.imapPass}
            onChange={(e) => handleChange('imapPass', e.target.value)}
          />
        </div>

        <div style={sectionTitle}>메일 템플릿</div>
        <div style={fieldGroup}>
          <label style={labelStyle}>제목 템플릿</label>
          <input
            style={inputStyle}
            value={form.subjectTemplate || DEFAULT_SUBJECT_TEMPLATE}
            onChange={(e) => handleChange('subjectTemplate', e.target.value)}
          />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>본문 템플릿</label>
          <textarea
            style={textareaStyle}
            value={form.bodyTemplate || DEFAULT_BODY_TEMPLATE}
            onChange={(e) => handleChange('bodyTemplate', e.target.value)}
          />
        </div>

        <div style={fieldGroup}>
          <label style={labelStyle}>발송 간격 (ms)</label>
          <input
            type="number"
            style={inputStyle}
            value={form.sendDelayMs}
            onChange={(e) => handleChange('sendDelayMs', e.target.value)}
            min="0"
          />
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
            대량 발송 시 서버 과부하를 피하기 위한 간격입니다. 예) 2000 = 2초
          </span>
        </div>

        <button type="submit" style={buttonStyle} disabled={loading}>
          {loading ? '저장 중...' : '설정 저장'}
        </button>

        {message && (
          <div
            style={{
              ...infoBox,
              background: message.type === 'error' ? '#fef2f2' : '#ecfdf5',
              border: `1px solid ${message.type === 'error' ? '#fca5a5' : '#a7f3d0'}`,
              color: message.type === 'error' ? '#b91c1c' : '#047857',
            }}
          >
            {message.text}
          </div>
        )}
      </form>

        <div
          style={{
            ...infoBox,
            marginTop: '24px',
            background: '#f1f5f9',
            border: '1px solid #cbd5f5',
            color: '#475569',
          }}
        >
          • 템플릿에서는 {'{{name}}'}, {'{{project}}'}, {'{{year}}'}, {'{{month}}'}, {'{{netPay}}'} 변수를 사용할 수 있습니다.
          <br />
          • 기본 본문 템플릿에는 “첨부 PDF 비밀번호는 생년월일(YYMMDD)입니다.” 안내 문구가 포함되어 있으며, 필요에 따라 수정하실 수 있습니다.
          <br />
          • 발송 결과는 하단 발송 결과 섹션에서 실시간으로 확인할 수 있습니다.
        </div>
    </div>
  );
}

export default PayrollEmailSettingsPage;

